<template>
	<div class="cq-page">
		<!-- 顶部品牌栏 -->
		<div class="cq-header">
			<div class="cq-header-left">
				<div class="cq-header-icon"><Icon type="ios-call" /></div>
				<div>
					<div class="cq-header-title">通话质检工作台</div>
					<div class="cq-header-sub">实时监控通话质量 · AI 转写 · 违规词</div>
				</div>
			</div>
			<div class="cq-header-right">
				<Tag class="cq-header-tag" color="blue">
					<Icon type="md-time" />
					<span>{{ dateRange[0] || '-' }} ~ {{ dateRange[1] || '-' }}</span>
				</Tag>
				<Button size="small" icon="md-sync" @click="refreshAll" class="cq-header-refresh">刷新数据</Button>
			</div>
		</div>

		<!-- 数据筛选 -->
		<Card class="cq-card">
			<div slot="title" class="cq-card-title">
				<span class="cq-card-title-icon"><Icon type="md-funnel" /></span>
				数据筛选
			</div>
			<div slot="extra" class="cq-card-extra">
				<Button type="primary" icon="md-search" @click="onSearch" size="small">查询</Button>
				<Button icon="md-refresh" @click="onReset" size="small" class="cq-btn-reset">重置</Button>
			</div>
			<div class="cq-filter-tip">
				<Icon type="md-information-circle" /> 日期范围会自动同步到下方图表与排行
			</div>
			<div class="cq-filter">
				<div class="cq-filter-col">
					<div class="cq-filter-item">
						<label>日期范围</label>
						<DatePicker type="daterange" :value="dateRange" @on-change="onDateChange"
							placeholder="选择起止日期" class="cq-filter-date" />
					</div>
				</div>
				<div class="cq-filter-col">
					<div class="cq-filter-item">
						<label>手机号</label>
						<Input v-model="filters.mobile" placeholder="输入完整手机号" />
					</div>
				</div>
				<div class="cq-filter-col">
					<div class="cq-filter-item">
						<label>客户名</label>
						<Input v-model="filters.client_name" placeholder="模糊匹配" />
					</div>
				</div>
				<div class="cq-filter-col">
					<div class="cq-filter-item">
						<label>拨打人</label>
						<Select v-model="filters.uid" clearable filterable placeholder="全部" class="cq-filter-select">
							<Option v-for="u in dialerOptions" :key="u.id" :value="u.id">{{ u.name }}</Option>
						</Select>
					</div>
				</div>
				<div class="cq-filter-col">
					<div class="cq-filter-item">
						<label>通话状态</label>
						<Select v-model="filters.status" clearable placeholder="全部">
							<Option :value="0">拨号中</Option>
							<Option :value="1">已接通</Option>
							<Option :value="2">未接</Option>
						</Select>
					</div>
				</div>
				<div class="cq-filter-col">
					<div class="cq-filter-item">
						<label>AI 状态</label>
						<Select v-model="filters.ai_status" clearable placeholder="全部">
							<Option value="none">未分析</Option>
							<Option value="pending">分析中</Option>
							<Option value="completed">已完成</Option>
							<Option value="failed">失败</Option>
						</Select>
					</div>
				</div>
				<div class="cq-filter-col">
					<div class="cq-filter-item">
						<label>录音</label>
						<Select v-model="filters.has_audio" clearable placeholder="全部">
							<Option :value="1">有录音</Option>
							<Option :value="0">无录音</Option>
						</Select>
					</div>
				</div>
			</div>
		</Card>

		<!-- 统计卡 4 张 -->
		<div class="cq-stats">
			<div class="stat-card stat-card-blue">
				<div class="stat-card-bar"></div>
				<div class="stat-card-icon"><Icon type="ios-call" size="24" /></div>
				<div class="stat-card-body">
					<div class="stat-card-label">总通话数</div>
					<div class="stat-card-value">{{ stats.total_calls || 0 }}</div>
					<div class="stat-card-sub">
						<span class="stat-card-sub-dot stat-card-sub-dot-success"></span>
						已接通 {{ stats.connected_calls || 0 }} · 接通率 {{ stats.connected_rate || 0 }}%
					</div>
				</div>
			</div>
			<div class="stat-card stat-card-green">
				<div class="stat-card-bar"></div>
				<div class="stat-card-icon"><Icon type="md-mic" size="24" /></div>
				<div class="stat-card-body">
					<div class="stat-card-label">录音完成</div>
					<div class="stat-card-value">{{ stats.recording_count || 0 }}</div>
					<div class="stat-card-sub">
						<span class="stat-card-sub-dot stat-card-sub-dot-success"></span>
						完成率 {{ stats.recording_rate || 0 }}%
					</div>
				</div>
			</div>
			<div class="stat-card stat-card-purple">
				<div class="stat-card-bar"></div>
				<div class="stat-card-icon"><Icon type="md-bulb" size="24" /></div>
				<div class="stat-card-body">
					<div class="stat-card-label">AI 转写完成</div>
					<div class="stat-card-value">{{ stats.ai_completed || 0 }}</div>
					<div class="stat-card-sub">
						<span class="stat-card-sub-dot stat-card-sub-dot-success"></span>
						完成率 {{ stats.ai_completed_rate || 0 }}%
					</div>
				</div>
			</div>
			<div class="stat-card stat-card-red">
				<div class="stat-card-bar"></div>
				<div class="stat-card-icon"><Icon type="md-warning" size="24" /></div>
				<div class="stat-card-body">
					<div class="stat-card-label">违规通话</div>
					<div class="stat-card-value">{{ stats.violation_calls || 0 }}</div>
					<div class="stat-card-sub">
						<span class="stat-card-sub-dot stat-card-sub-dot-danger"></span>
						违规率 {{ stats.violation_rate || 0 }}%
					</div>
				</div>
			</div>
		</div>

		<!-- 图表区 -->
		<div class="cq-charts">
			<Card class="cq-card cq-chart-main">
				<div slot="title" class="cq-card-title">
					<span class="cq-card-title-icon cq-card-title-icon-blue"><Icon type="md-trending-up" /></span>
					通话趋势
					<span class="cq-card-tip">{{ dateRange[0] }} ~ {{ dateRange[1] }}</span>
				</div>
				<div ref="trendChart" class="chart-canvas chart-canvas-trend"></div>
			</Card>
			<Card class="cq-card cq-chart-side">
				<div slot="title" class="cq-card-title">
					<span class="cq-card-title-icon cq-card-title-icon-red"><Icon type="md-warning" /></span>
					违规词 TOP 10
				</div>
				<div ref="violationChart" class="chart-canvas chart-canvas-violation"></div>
			</Card>
		</div>

		<!-- 排行 Tab -->
		<Card class="cq-card">
			<div slot="title" class="cq-card-title">
				<span class="cq-card-title-icon cq-card-title-icon-orange"><Icon type="md-podium" /></span>
				质检排行
				<span class="cq-card-tip">按通话量倒序</span>
			</div>
			<Tabs v-model="rankingTab" @on-click="loadRanking" :animated="false">
				<TabPane label="拨打人排行" name="uid">
					<Table :columns="rankingColsUid" :data="rankingList" :loading="rankingLoading" size="small" stripe no-data-text="暂无数据"></Table>
				</TabPane>
				<TabPane label="部门排行" name="department">
					<Table :columns="rankingColsDept" :data="rankingList" :loading="rankingLoading" size="small" stripe no-data-text="暂无数据"></Table>
				</TabPane>
			</Tabs>
		</Card>

		<!-- 通话明细 -->
		<Card class="cq-card">
			<div slot="title" class="cq-card-title">
				<span class="cq-card-title-icon cq-card-title-icon-blue"><Icon type="md-list" /></span>
				通话明细
				<span class="cq-card-tip">共 {{ count }} 条</span>
			</div>

			<Table :columns="columns" :data="list" :loading="loading" size="small" stripe border>
				<template slot-scope="{ row }" slot="status">
					<span class="status-dot" :class="'status-dot-' + statusColor(row.status)"></span>
					<span class="status-text">{{ row.status_text }}</span>
				</template>
				<template slot-scope="{ row }" slot="audio">
					<Tag v-if="row.has_audio" color="success" size="small">有</Tag>
					<Tag v-else color="default" size="small">无</Tag>
				</template>
				<template slot-scope="{ row }" slot="ai_status">
					<Tag :color="aiColor(row.ai_status)" size="small">{{ row.ai_status_text }}</Tag>
				</template>
				<template slot-scope="{ row }" slot="action">
					<Button size="small" type="primary" ghost icon="md-eye" @click="openDetail(row)">查看</Button>
				</template>
			</Table>
			<div class="pager">
				<Page show-total :total="count" show-sizer :page-size="filters.limit"
					:page-size-opts="pageSizeOpts" :current="filters.page"
					@on-change="onPageChange" @on-page-size-change="changeSize"
					size="small" show-elevator />
			</div>
		</Card>

		<!-- 详情抽屉 -->
		<Drawer v-model="drawer" :width="880" :mask-closable="false" class="cq-drawer" :scrollable="true">
			<div slot="header" class="cq-drawer-header" v-if="current.id">
				<div class="cq-drawer-header-left">
					<div class="cq-drawer-id">#{{ current.id }}</div>
					<div class="cq-drawer-name">
						{{ current.client_name || '未知客户' }}
						<span class="cq-drawer-mobile">{{ current.mobile || '-' }}</span>
					</div>
				</div>
				<div class="cq-drawer-header-right">
					<Tag :color="statusColor(current.status)" size="medium">{{ current.status_text }}</Tag>
					<Tag v-if="current.ai && current.ai.id" :color="aiColor(current.ai.status)" size="medium">
						AI {{ current.ai.status_text }}
					</Tag>
				</div>
			</div>

			<div v-if="current.id" class="cq-drawer-body">
				<!-- 概览 4 项 -->
				<div class="cq-overview">
					<div class="cq-overview-item">
						<div class="cq-overview-label">拨打人</div>
						<div class="cq-overview-value">{{ current.dialer_name || '-' }}</div>
						<div class="cq-overview-extra">{{ current.department_name || '未分配部门' }}</div>
					</div>
					<div class="cq-overview-item">
						<div class="cq-overview-label">通话时长</div>
						<div class="cq-overview-value cq-overview-value-large">{{ formatDuration(current.duration) }}</div>
					</div>
					<div class="cq-overview-item">
						<div class="cq-overview-label">拨号时间</div>
						<div class="cq-overview-value">{{ current.dial_time || '-' }}</div>
						<div class="cq-overview-extra">接通 {{ current.connect_time || '-' }}</div>
					</div>
					<div class="cq-overview-item">
						<div class="cq-overview-label">挂断时间</div>
						<div class="cq-overview-value">{{ current.end_time || '-' }}</div>
					</div>
				</div>

				<!-- 左右两栏 -->
				<div class="cq-cols">
					<!-- 左：通话信息 + 录音 -->
					<div class="cq-col">
						<div class="cq-section">
							<div class="cq-section-head">
								<span class="cq-section-icon cq-section-icon-blue"><Icon type="md-call" size="14" /></span>
								<span class="cq-section-title">通话信息</span>
							</div>
							<div class="cq-section-body">
								<div class="cq-info-list">
									<div class="cq-info-row">
										<span class="cq-info-label">客户</span>
										<span class="cq-info-value">{{ current.client_name || '-' }}</span>
									</div>
									<div class="cq-info-row">
										<span class="cq-info-label">手机号</span>
										<span class="cq-info-value">{{ current.mobile || '-' }}</span>
									</div>
									<div class="cq-info-row">
										<span class="cq-info-label">拨打人</span>
										<span class="cq-info-value">{{ current.dialer_name || '-' }}</span>
									</div>
									<div class="cq-info-row">
										<span class="cq-info-label">部门</span>
										<span class="cq-info-value">{{ current.department_name || '-' }}</span>
									</div>
								</div>
							</div>
						</div>

						<div class="cq-section">
							<div class="cq-section-head">
								<span class="cq-section-icon cq-section-icon-green"><Icon type="md-musical-note" size="14" /></span>
								<span class="cq-section-title">通话录音</span>
							</div>
							<div class="cq-section-body">
								<audio v-if="current.has_audio" :src="current.recording_url" controls class="cq-audio"></audio>
								<div v-else class="cq-empty-block">
									<Icon type="md-musical-notes" size="28" />
									<div>该通话暂无录音</div>
								</div>
							</div>
						</div>
					</div>

					<!-- 右：AI 分析 -->
					<div class="cq-col">
						<div class="cq-section">
							<div class="cq-section-head">
								<span class="cq-section-icon cq-section-icon-purple"><Icon type="md-bulb" size="14" /></span>
								<span class="cq-section-title">AI 分析</span>
								<Button v-if="canRetryAi" size="small" type="warning" ghost icon="md-refresh-circle"
									@click="retryAi" :loading="retryting" class="cq-section-btn">
									重新分析
								</Button>
							</div>
							<div class="cq-section-body">
								<div v-if="current.ai && current.ai.id" class="cq-ai">
									<div class="cq-ai-meta">
										<div class="cq-ai-meta-item">
											<span class="cq-info-label">状态</span>
											<Tag :color="aiColor(current.ai.status)" size="small">{{ current.ai.status_text }}</Tag>
										</div>
										<div v-if="current.ai.process_duration" class="cq-ai-meta-item">
											<span class="cq-info-label">耗时</span>
											<span class="cq-info-value">{{ current.ai.process_duration }} s</span>
										</div>
										<div v-if="current.ai.score !== undefined && current.ai.score !== null" class="cq-ai-meta-item">
											<span class="cq-info-label">评分</span>
											<span class="cq-info-value cq-info-value-strong">{{ current.ai.score }}</span>
										</div>
									</div>

									<div v-if="current.ai.error" class="cq-ai-error">
										<Icon type="md-warning" />
										{{ current.ai.error }}
									</div>

									<!-- 违规词 -->
									<div class="cq-tag-group">
										<div class="cq-tag-group-head">
											<Icon type="md-warning" />
											<span>违规词</span>
											<span class="cq-tag-group-count">{{ (current.ai.violation_words || []).length }}</span>
										</div>
										<div class="cq-tag-group-body">
											<Tag v-for="(w, i) in current.ai.violation_words" :key="'v' + i"
												color="error" class="cq-tag-violation">
												{{ typeof w === 'string' ? w : (w.word || w.name || JSON.stringify(w)) }}
											</Tag>
											<span v-if="!current.ai.violation_words || !current.ai.violation_words.length" class="cq-empty-inline">
								无
											</span>
										</div>
									</div>

									<!-- 客户需求 -->
									<div class="cq-tag-group">
										<div class="cq-tag-group-head cq-tag-group-head-green">
											<Icon type="md-checkmark-circle" />
											<span>客户需求</span>
											<span class="cq-tag-group-count">{{ (current.ai.needs_analysis || []).length }}</span>
										</div>
										<div class="cq-tag-group-body">
											<Tag v-for="(n, i) in current.ai.needs_analysis" :key="'n' + i"
												color="success" class="cq-tag-need">
												{{ typeof n === 'string' ? n : (n.tag || n.text || n.name || JSON.stringify(n)) }}
											</Tag>
											<span v-if="!current.ai.needs_analysis || !current.ai.needs_analysis.length" class="cq-empty-inline">
								无
											</span>
										</div>
									</div>

									<!-- 对话气泡 -->
									<div v-if="current.ai.dialogues && current.ai.dialogues.length" class="cq-dialogue-block">
										<div class="cq-dialogue-head">对话明细</div>
										<div class="cq-dialogue-list">
											<div v-for="(d, i) in current.ai.dialogues" :key="'d' + i"
												class="cq-bubble" :class="bubbleClass(d.speaker, i)">
												<div class="cq-bubble-meta">
													<span class="cq-bubble-name">{{ d.speaker || bubbleName(d.speaker, i) }}</span>
												</div>
												<div class="cq-bubble-text" v-html="highlightText(d.text || d.content || '', current.ai.violation_words)"></div>
											</div>
										</div>
									</div>

									<!-- 纯转写文本（无对话明细时显示） -->
									<div v-if="(!current.ai.dialogues || !current.ai.dialogues.length) && current.ai.text" class="cq-dialogue-block">
										<div class="cq-dialogue-head">转写文本</div>
										<div class="cq-ai-text" v-html="highlightText(current.ai.text, current.ai.violation_words)"></div>
									</div>
								</div>

								<div v-else class="cq-empty-block">
									<Icon type="md-information-circle" size="28" />
									<div>该通话暂无 AI 分析结果</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="drawer-footer" slot="footer">
				<Button @click="drawer = false">关闭</Button>
			</div>
		</Drawer>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
	getCallQualityIndex,
	getCallQualityList,
	getCallQualityDetail,
	getCallQualityTrend,
	getCallQualityRanking,
	retryCallQualityAi,
} from '@/api/callQuality'
import { getUserList } from '@/api/user'

export default {
	name: 'call_quality',
	computed: {
		...mapGetters(['token']),
		canRetryAi() {
			if (!this.current || !this.current.id) return false
			const ai = this.current.ai
			if (!ai || !ai.id) return true
			return ai.status === 'failed' || ai.status === ''
		},
	},
	data() {
		return {
			loading: false,
			rankingLoading: false,
			retryting: false,
			dateRange: [],
			stats: {},
			list: [],
			count: 0,
			pageSizeOpts: [10, 50, 100, 500],
			dialerOptions: [],
			filters: {
				start_date: '',
				end_date: '',
				uid: null,
				status: null,
				ai_status: '',
				has_audio: null,
				mobile: '',
				client_name: '',
				page: 1,
				limit: 20,
			},
			columns: [
				{ title: '拨号时间', key: 'dial_time', width: 145, fixed: 'left' },
				{ title: '客户', key: 'client_name', minWidth: 90 },
				{ title: '手机号', key: 'mobile', width: 120 },
				{ title: '拨打人', key: 'dialer_name', width: 100 },
				{ title: '部门', key: 'department_name', width: 100 },
				{ title: '时长', key: 'duration', width: 80,
					render: (h, p) => h('span', this.formatDuration(p.row.duration)) },
				{ title: '状态', slot: 'status', width: 90, align: 'center' },
				{ title: '录音', slot: 'audio', width: 70, align: 'center' },
				{ title: 'AI 状态', slot: 'ai_status', width: 100, align: 'center' },
				{ title: '操作', slot: 'action', width: 90, align: 'center', fixed: 'right' },
			],
			rankingTab: 'uid',
			rankingList: [],
			rankingColsUid: [
				{ title: '排名', key: 'rank', width: 80, align: 'center',
					render: (h, p) => this.renderRank(h, p) },
				{ title: '拨打人', key: 'name', minWidth: 100 },
				{ title: '部门', key: 'department_name', minWidth: 110 },
				{ title: '通话数', key: 'total_calls', width: 90, sortable: true, align: 'right' },
				{ title: '接通率', key: 'connected_rate', width: 220,
					render: (h, p) => this.renderRate(h, p) },
				{ title: '平均时长', key: 'avg_duration', width: 100, align: 'right',
					render: (h, p) => h('span', this.formatDuration(p.row.avg_duration)) },
				{ title: '违规次数', key: 'violation_calls', width: 90, align: 'right',
					render: (h, p) => this.renderDanger(h, p, 'violation_calls') },
				{ title: 'AI完成', key: 'ai_completed', width: 90, align: 'right' },
			],
			rankingColsDept: [
				{ title: '排名', key: 'rank', width: 80, align: 'center',
					render: (h, p) => this.renderRank(h, p) },
				{ title: '部门', key: 'name', minWidth: 110 },
				{ title: '通话数', key: 'total_calls', width: 90, sortable: true, align: 'right' },
				{ title: '接通率', key: 'connected_rate', width: 220,
					render: (h, p) => this.renderRate(h, p) },
				{ title: '平均时长', key: 'avg_duration', width: 100, align: 'right',
					render: (h, p) => h('span', this.formatDuration(p.row.avg_duration)) },
				{ title: '违规次数', key: 'violation_calls', width: 90, align: 'right',
					render: (h, p) => this.renderDanger(h, p, 'violation_calls') },
				{ title: 'AI完成', key: 'ai_completed', width: 90, align: 'right' },
			],
			drawer: false,
			current: {},
			trendChart: null,
			violationChart: null,
		}
	},
	mounted() {
		const today = new Date()
		const monthAgo = new Date()
		monthAgo.setDate(today.getDate() - 29)
		this.dateRange = [this.formatDate(monthAgo), this.formatDate(today)]
		this.filters.start_date = this.dateRange[0]
		this.filters.end_date = this.dateRange[1]
		this.refreshAll()
		this.loadDialers()
		this.$nextTick(() => this.initCharts())
		window.addEventListener('resize', this.handleResize)
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.handleResize)
		if (this.trendChart) { this.trendChart.dispose(); this.trendChart = null }
		if (this.violationChart) { this.violationChart.dispose(); this.violationChart = null }
	},
	methods: {
		formatDate(d) {
			const y = d.getFullYear()
			const m = String(d.getMonth() + 1).padStart(2, '0')
			const day = String(d.getDate()).padStart(2, '0')
			return `${y}-${m}-${day}`
		},
		formatDuration(sec) {
			sec = Number(sec) || 0
			if (sec <= 0) return '-'
			const m = Math.floor(sec / 60)
			const s = sec % 60
			return `${m}'${String(s).padStart(2, '0')}"`
		},
		statusColor(status) {
			return ['', 'success', 'warning'][Number(status)] || 'default'
		},
		aiColor(status) {
			const m = { pending: 'blue', processing: 'blue', completed: 'success', failed: 'error', '': 'default' }
			return m[status] || 'default'
		},
		renderRank(h, p) {
			const rank = p.row.rank
			const cls = ['rank-medal']
			let inner = rank
			if (rank === 1) { cls.push('rank-medal-gold'); inner = '🥇' }
			else if (rank === 2) { cls.push('rank-medal-silver'); inner = '🥈' }
			else if (rank === 3) { cls.push('rank-medal-bronze'); inner = '🥉' }
			return h('div', { class: cls.join(' ') }, [
				h('span', { class: 'rank-medal-num' }, rank),
				h('span', { class: 'rank-medal-icon' }, inner),
			])
		},
		renderDanger(h, p, key) {
			const val = p.row[key]
			if (val > 0) {
				return h('span', { class: 'cell-danger' }, val)
			}
			return h('span', { class: 'cell-muted' }, val)
		},
		renderRate(h, p) {
			const rate = Number(p.row.connected_rate) || 0
			const color = rate >= 60 ? 'success' : (rate >= 30 ? 'warning' : 'error')
			return h('div', { class: 'rate-cell' }, [
				h('Progress', {
					props: { percent: rate, strokeWidth: 6, status: color }
				}),
				h('span', { class: 'rate-num' }, rate + '%')
			])
		},
		bubbleClass(speaker, idx) {
			const s = String(speaker || '').toLowerCase()
			// 业务员/客户/agent/customer 判定；无法判断时按奇偶交替
			if (/客户|customer|client|user/.test(s)) return 'cq-bubble-client'
			if (/业务员|agent|staff|坐席|sales/.test(s)) return 'cq-bubble-agent'
			return idx % 2 ? 'cq-bubble-client' : 'cq-bubble-agent'
		},
		bubbleName(speaker, idx) {
			const s = String(speaker || '').toLowerCase()
			if (/客户|customer|client|user/.test(s)) return '客户'
			if (/业务员|agent|staff|坐席|sales/.test(s)) return '业务员'
			return idx % 2 ? '客户' : '业务员'
		},
		onDateChange(v) {
			this.dateRange = v || []
			this.filters.start_date = v && v[0] ? v[0] : ''
			this.filters.end_date = v && v[1] ? v[1] : ''
		},
		onSearch() {
			this.filters.page = 1
			this.loadStats()
			this.loadList()
			this.loadTrend()
			this.loadRanking()
		},
		onReset() {
			this.filters = {
				start_date: '', end_date: '',
				uid: null, status: null, ai_status: '',
				has_audio: null, mobile: '', client_name: '',
				page: 1, limit: 20,
			}
			const today = new Date()
			const monthAgo = new Date()
			monthAgo.setDate(today.getDate() - 29)
			this.dateRange = [this.formatDate(monthAgo), this.formatDate(today)]
			this.filters.start_date = this.dateRange[0]
			this.filters.end_date = this.dateRange[1]
			this.onSearch()
		},
		refreshAll() {
			this.loadStats()
			this.loadList()
			this.loadTrend()
			this.loadRanking()
		},
		onPageChange(p) {
			this.filters.page = p
			this.loadList()
		},
		changeSize(n) {
			this.filters.limit = n
			this.filters.page = 1
			this.loadList()
		},
		async loadStats() {
			const res = await getCallQualityIndex(this, {
				token: this.token,
				start_date: this.filters.start_date,
				end_date: this.filters.end_date,
			})
			if (res && res.code === 1) {
				this.stats = res.data || {}
			}
		},
		async loadList() {
			this.loading = true
			try {
				const res = await getCallQualityList(this, {
					token: this.token,
					...this.filters,
				})
				if (res && res.code === 1) {
					this.list = (res.data && res.data.list) || []
					this.count = (res.data && res.data.count) || 0
				}
			} finally {
				this.loading = false
			}
		},
		async loadDialers() {
			try {
				const res = await getUserList(this, { token: this.token })
				if (res && res.code === 1 && res.data && Array.isArray(res.data.list)) {
					this.dialerOptions = res.data.list.map(u => ({
						id: u.value || u.id,
						name: u.label || u.name || u.phone,
					}))
				}
			} catch (e) { /* 拨打人下拉加载失败不影响主流程 */ }
		},
		async loadTrend() {
			const res = await getCallQualityTrend(this, {
				token: this.token,
				start_date: this.filters.start_date,
				end_date: this.filters.end_date,
			})
			if (res && res.code === 1 && res.data) {
				this.renderTrendChart(res.data)
				this.renderViolationChart(res.data)
			}
		},
		async loadRanking() {
			this.rankingLoading = true
			try {
				const res = await getCallQualityRanking(this, {
					token: this.token,
					start_date: this.filters.start_date,
					end_date: this.filters.end_date,
					type: this.rankingTab,
					limit: 20,
				})
				if (res && res.code === 1 && res.data) {
					this.rankingList = res.data.list || []
				}
			} finally {
				this.rankingLoading = false
			}
		},
		async openDetail(row) {
			this.drawer = true
			const res = await getCallQualityDetail(this, { token: this.token, id: row.id })
			if (res && res.code === 1) {
				this.current = res.data || {}
			} else {
				this.current = { id: row.id, mobile: row.mobile }
			}
		},
		async retryAi() {
			if (!this.current.id) return
			this.retryting = true
			try {
				const res = await retryCallQualityAi(this, { token: this.token, id: this.current.id })
				if (res && res.code === 1) {
					this.$Message.success(res.msg || '已提交 AI 重新分析')
					// 关闭抽屉让用户去列表刷新
					setTimeout(() => { this.drawer = false; this.loadList() }, 800)
				} else {
					this.$Message.error(res.msg || '提交失败')
				}
			} finally {
				this.retryting = false
			}
		},
		initCharts() {
			if (!this.$refs.trendChart || !this.$refs.violationChart) return
			this.trendChart = this.$echarts.init(this.$refs.trendChart)
			this.violationChart = this.$echarts.init(this.$refs.violationChart)
			// 先用占位 option，加载完后覆盖
			this.trendChart.setOption({
				title: { text: '加载中…', left: 'center', top: 'middle', textStyle: { color: '#999' } }
			})
			this.violationChart.setOption({
				title: { text: '加载中…', left: 'center', top: 'middle', textStyle: { color: '#999' } }
			})
			this.loadTrend()
		},
		renderTrendChart(data) {
			if (!this.trendChart) return
			this.trendChart.setOption({
				color: ['#1890ff', '#52c41a', '#722ed1', '#ff4d4f'],
				tooltip: {
					trigger: 'axis', axisPointer: { type: 'cross', lineStyle: { color: '#e5e7eb' } },
					backgroundColor: 'rgba(255,255,255,0.96)',
					borderColor: '#e5e7eb',
					textStyle: { color: '#1f2937' },
					extraCssText: 'box-shadow: 0 4px 16px rgba(0,0,0,0.08); border-radius: 6px;',
				},
				legend: { data: ['通话量', '接通量', 'AI完成', '违规量'], top: 0, icon: 'roundRect' },
				grid: { left: 50, right: 30, bottom: 30, top: 40 },
				xAxis: {
					type: 'category',
					data: data.dates || [],
					boundaryGap: true,
					axisLine: { lineStyle: { color: '#e5e7eb' } },
					axisLabel: {
						formatter: v => v ? v.substring(5) : '',
						color: '#6b7280',
					},
					axisTick: { show: false },
				},
				yAxis: [
					{ type: 'value', name: '数量', position: 'left',
						axisLine: { show: false },
						axisLabel: { color: '#6b7280' },
						splitLine: { lineStyle: { color: '#f0f2f5', type: 'dashed' } },
						nameTextStyle: { color: '#9ca3af' },
					},
				],
				series: [
					{
						name: '通话量', type: 'bar', data: data.totals || [],
						itemStyle: { color: '#1890ff', borderRadius: [4, 4, 0, 0] },
						barWidth: '28%',
						emphasis: { focus: 'series' },
					},
					{
						name: '接通量', type: 'line', data: data.connected || [],
						smooth: true, symbol: 'circle', symbolSize: 6,
						itemStyle: { color: '#52c41a' }, lineStyle: { width: 2 },
						emphasis: { focus: 'series' },
					},
					{
						name: 'AI完成', type: 'line', data: data.ai_completed || [],
						smooth: true, symbol: 'circle', symbolSize: 6,
						itemStyle: { color: '#722ed1' }, lineStyle: { width: 2 },
						emphasis: { focus: 'series' },
					},
					{
						name: '违规量', type: 'line', data: data.violations || [],
						smooth: true, symbol: 'circle', symbolSize: 6,
						itemStyle: { color: '#ff4d4f' }, lineStyle: { width: 2 },
						emphasis: { focus: 'series' },
					},
				],
			}, true)
		},
		renderViolationChart(data) {
			if (!this.violationChart) return
			// 真实违规词词频：[{ name: '违规词', count: 次数 }] 取前 10
			const list = (data.violation_top || []).slice(0, 10)
			if (list.length === 0) {
				this.violationChart.setOption({
					title: { text: '所选时段暂无违规', left: 'center', top: 'middle', textStyle: { color: '#999', fontSize: 14 } }
				}, true)
				return
			}
			// 横向柱状图：Y 轴从上到下应是 count 大的在上，所以要 reverse
			const reversed = list.slice().reverse()
			this.violationChart.setOption({
				tooltip: {
					trigger: 'axis', axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(0,0,0,0.04)' } },
					backgroundColor: 'rgba(255,255,255,0.96)',
					borderColor: '#e5e7eb',
					textStyle: { color: '#1f2937' },
					extraCssText: 'box-shadow: 0 4px 16px rgba(0,0,0,0.08); border-radius: 6px;',
					formatter: params => {
						const p = params[0]
						return `${p.name}<br/>出现 ${p.value} 次`
					}
				},
				grid: { left: 90, right: 50, top: 10, bottom: 20 },
				xAxis: {
					type: 'value', minInterval: 1,
					axisLine: { show: false },
					axisLabel: { color: '#6b7280' },
					splitLine: { lineStyle: { color: '#f0f2f5', type: 'dashed' } },
				},
				yAxis: {
					type: 'category',
					data: reversed.map(x => x.name),
					axisLine: { show: false },
					axisTick: { show: false },
					axisLabel: { fontSize: 12, color: '#374151' },
				},
				series: [{
					name: '违规次数',
					type: 'bar',
					data: reversed.map(x => x.count),
					itemStyle: {
						color: { type: 'linear', x: 0, y: 0, x2: 1, y2: 0,
							colorStops: [{ offset: 0, color: '#ff7875' }, { offset: 1, color: '#ff4d4f' }] },
						borderRadius: [0, 4, 4, 0],
					},
					label: { show: true, position: 'right', color: '#6b7280', fontSize: 11 },
					barMaxWidth: 18,
				}],
			}, true)
		},
		handleResize() {
			this.trendChart && this.trendChart.resize()
			this.violationChart && this.violationChart.resize()
		},
		escapeHtml(s) {
			if (s == null) return ''
			return String(s).replace(/[&<>"']/g, c => ({
				'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
			}[c]))
		},
		escapeRegExp(s) {
			return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
		},
		extractViolationWords(words) {
			if (!Array.isArray(words)) return []
			return words.map(w => {
				if (typeof w === 'string') return w
				return w.word || w.name || w.text || ''
			}).filter(Boolean)
		},
		// 把违规词在文本中红色背景高亮（先转义防 XSS）
		highlightText(text, words) {
			if (!text) return ''
			const escaped = this.escapeHtml(text)
			const list = this.extractViolationWords(words)
			if (!list.length) return escaped
			let out = escaped
			list.forEach(w => {
				const safe = this.escapeHtml(w)
				const re = new RegExp(this.escapeRegExp(safe), 'gi')
				out = out.replace(re, `<mark class="violation-mark">${safe}</mark>`)
			})
			return out
		},
	},
}
</script>

<style scoped>
/* ============ 设计令牌 ============ */
.cq-page {
	--cq-primary: #1890ff;
	--cq-success: #52c41a;
	--cq-warning: #faad14;
	--cq-danger: #ff4d4f;
	--cq-purple: #722ed1;
	--cq-orange: #fa8c16;
	--cq-text-1: #1f2937;
	--cq-text-2: #374151;
	--cq-text-3: #6b7280;
	--cq-text-4: #9ca3af;
	--cq-border: #e5e7eb;
	--cq-border-light: #f0f2f5;
	--cq-bg: #f5f7fa;
	--cq-card-bg: #ffffff;
	--cq-shadow: 0 1px 2px rgba(0,0,0,0.04);
	--cq-shadow-hover: 0 4px 12px rgba(0,0,0,0.08);

	padding: 24px;
	background: var(--cq-bg);
	min-height: calc(100vh - 60px);
	max-width: 1680px;
	margin: 0 auto;
}
.cq-page, .cq-page * { box-sizing: border-box; }

/* ============ 顶部品牌栏 ============ */
.cq-header {
	background: var(--cq-card-bg);
	border-radius: 8px;
	padding: 20px 24px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 18px;
	box-shadow: var(--cq-shadow);
	border-left: 4px solid var(--cq-primary);
}
.cq-header-left { display: flex; align-items: center; gap: 16px; }
.cq-header-icon {
	width: 44px; height: 44px;
	background: var(--cq-primary);
	color: #fff;
	border-radius: 8px;
	display: flex; align-items: center; justify-content: center;
	font-size: 22px;
	flex-shrink: 0;
}
.cq-header-title { font-size: 18px; font-weight: 600; color: var(--cq-text-1); line-height: 1.4; }
.cq-header-sub { font-size: 12px; color: var(--cq-text-3); margin-top: 4px; }
.cq-header-right { display: flex; align-items: center; gap: 12px; }
.cq-header-tag { font-size: 12px; padding: 6px 12px; }
.cq-header-tag .ivu-icon { margin-right: 4px; }
.cq-header-refresh { font-size: 12px; }

/* ============ 卡片标题 ============ */
.cq-card { border-radius: 8px; margin-bottom: 18px; border: 1px solid var(--cq-border); }
.cq-card >>> .ivu-card-head { border-bottom: 1px solid var(--cq-border-light); padding: 14px 20px; }
.cq-card >>> .ivu-card-body { padding: 20px; }
.cq-card-title {
	display: flex; align-items: center; gap: 8px;
	font-weight: 600; color: var(--cq-text-1); font-size: 14px;
}
.cq-card-title-icon {
	width: 22px; height: 22px;
	display: inline-flex; align-items: center; justify-content: center;
	background: var(--cq-bg); color: var(--cq-text-3);
	border-radius: 5px;
	font-size: 13px;
}
.cq-card-title-icon-blue { background: rgba(24,144,255,0.1); color: var(--cq-primary); }
.cq-card-title-icon-red { background: rgba(255,77,79,0.1); color: var(--cq-danger); }
.cq-card-title-icon-orange { background: rgba(250,140,22,0.1); color: var(--cq-orange); }
.cq-card-tip {
	font-weight: 400; color: var(--cq-text-4); font-size: 12px;
	margin-left: auto;
}

/* ============ 筛选区 ============ */
.cq-card-extra { display: flex; align-items: center; gap: 8px; }
.cq-filter-tip {
	font-size: 12px; color: var(--cq-text-3);
	margin-bottom: 16px;
	display: flex; align-items: center; gap: 4px;
}
.cq-filter-tip .ivu-icon { color: var(--cq-primary); }
.cq-filter {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 14px 16px;
}
.cq-filter-col { min-width: 0; }
.cq-filter-item {
	display: flex; align-items: center; gap: 8px;
	height: 34px;
}
.cq-filter-item label {
	font-size: 13px; color: var(--cq-text-3);
	white-space: nowrap; min-width: 56px; flex-shrink: 0;
}
.cq-filter-item >>> .ivu-input-wrapper,
.cq-filter-item >>> .ivu-select,
.cq-filter-item >>> .ivu-date-picker { flex: 1; min-width: 0; }
.cq-filter-date { width: 100%; }
.cq-filter-select { width: 100%; }
.cq-btn-reset { margin-left: 4px; }

/* ============ 统计卡 ============ */
.cq-stats {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 16px;
	margin-bottom: 18px;
}
.stat-card {
	background: var(--cq-card-bg);
	border-radius: 8px;
	padding: 22px 24px;
	display: flex;
	align-items: center;
	gap: 16px;
	position: relative;
	overflow: hidden;
	box-shadow: var(--cq-shadow);
	border: 1px solid var(--cq-border-light);
	transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.stat-card:hover { transform: translateY(-2px); box-shadow: var(--cq-shadow-hover); }
.stat-card-bar {
	position: absolute;
	left: 0; top: 0; bottom: 0;
	width: 4px;
}
.stat-card-blue .stat-card-bar { background: var(--cq-primary); }
.stat-card-blue .stat-card-icon { background: rgba(24,144,255,0.1); color: var(--cq-primary); }
.stat-card-green .stat-card-bar { background: var(--cq-success); }
.stat-card-green .stat-card-icon { background: rgba(82,196,26,0.1); color: var(--cq-success); }
.stat-card-purple .stat-card-bar { background: var(--cq-purple); }
.stat-card-purple .stat-card-icon { background: rgba(114,46,209,0.1); color: var(--cq-purple); }
.stat-card-red .stat-card-bar { background: var(--cq-danger); }
.stat-card-red .stat-card-icon { background: rgba(255,77,79,0.1); color: var(--cq-danger); }
.stat-card-icon {
	width: 48px; height: 48px;
	border-radius: 8px;
	display: flex; align-items: center; justify-content: center;
	flex-shrink: 0;
}
.stat-card-body { flex: 1; min-width: 0; }
.stat-card-label { font-size: 13px; color: var(--cq-text-3); margin-bottom: 6px; }
.stat-card-value {
	font-size: 28px; font-weight: 600; color: var(--cq-text-1);
	line-height: 1.2;
	font-feature-settings: 'tnum';
	letter-spacing: -0.5px;
}
.stat-card-sub {
	font-size: 12px; color: var(--cq-text-3);
	margin-top: 8px;
	display: flex; align-items: center; gap: 5px;
}
.stat-card-sub-dot {
	width: 6px; height: 6px; border-radius: 50%;
	display: inline-block; flex-shrink: 0;
}
.stat-card-sub-dot-success { background: var(--cq-success); }
.stat-card-sub-dot-danger { background: var(--cq-danger); }

/* ============ 图表区 ============ */
.cq-charts {
	display: grid;
	grid-template-columns: 2fr 1fr;
	gap: 16px;
	margin-bottom: 18px;
}
.cq-charts .cq-card { margin-bottom: 0; }
.cq-chart-main, .cq-chart-side { min-width: 0; }
.chart-canvas { width: 100%; }
.chart-canvas-trend { height: 340px; }
.chart-canvas-violation { height: 340px; }

/* ============ 表格 ============ */
.pager { text-align: right; margin-top: 14px; }

/* 排名徽章 */
.rank-medal {
	display: inline-flex; align-items: center; gap: 4px;
	justify-content: center;
}
.rank-medal-num { font-weight: 600; }
.rank-medal-icon { font-size: 14px; }
.rank-medal-gold .rank-medal-num { color: #d48806; }
.rank-medal-silver .rank-medal-num { color: #8c8c8c; }
.rank-medal-bronze .rank-medal-num { color: #ad6800; }

.cell-danger { color: var(--cq-danger); font-weight: 600; }
.cell-muted { color: var(--cq-text-4); }
.rate-cell { display: flex; align-items: center; gap: 8px; }
.rate-num { color: var(--cq-text-3); font-size: 12px; min-width: 40px; text-align: right; }

/* 状态点 */
.status-dot {
	display: inline-block; width: 6px; height: 6px;
	border-radius: 50%; margin-right: 6px;
	vertical-align: middle;
}
.status-dot-default { background: var(--cq-text-4); }
.status-dot-success { background: var(--cq-success); }
.status-dot-warning { background: var(--cq-warning); }
.status-dot-error { background: var(--cq-danger); }
.status-text { color: var(--cq-text-2); font-size: 13px; }

/* ============ 详情抽屉 ============ */
.cq-drawer >>> .ivu-drawer-body { padding: 0; }
.cq-drawer-header {
	display: flex; justify-content: space-between; align-items: center;
	padding: 4px 0; width: 100%;
}
.cq-drawer-header-left { display: flex; align-items: center; gap: 12px; }
.cq-drawer-id {
	font-size: 12px; color: var(--cq-text-4);
	background: var(--cq-bg);
	padding: 2px 8px;
	border-radius: 4px;
	font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.cq-drawer-name {
	font-size: 16px; font-weight: 600; color: var(--cq-text-1);
	display: flex; align-items: center; gap: 8px;
}
.cq-drawer-mobile {
	font-weight: 400; font-size: 13px;
	color: var(--cq-text-3);
	font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.cq-drawer-header-right { display: flex; gap: 6px; }
.cq-drawer-body { padding: 16px 20px 20px; }

/* 概览 4 项 */
.cq-overview {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 12px;
	background: var(--cq-bg);
	border-radius: 8px;
	padding: 14px 16px;
	margin-bottom: 16px;
}
.cq-overview-item { min-width: 0; }
.cq-overview-label {
	font-size: 12px; color: var(--cq-text-3);
	margin-bottom: 4px;
}
.cq-overview-value {
	font-size: 14px; color: var(--cq-text-1); font-weight: 500;
	font-feature-settings: 'tnum';
	white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cq-overview-value-large {
	font-size: 18px; font-weight: 600;
	color: var(--cq-primary);
}
.cq-overview-extra {
	font-size: 12px; color: var(--cq-text-4); margin-top: 2px;
	white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

/* 左右两栏 */
.cq-cols {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 14px;
}
.cq-col { min-width: 0; }

/* 区块 */
.cq-section {
	border: 1px solid var(--cq-border-light);
	border-radius: 8px;
	background: var(--cq-card-bg);
	overflow: hidden;
	margin-bottom: 14px;
}
.cq-section:last-child { margin-bottom: 0; }
.cq-section-head {
	display: flex; align-items: center; gap: 8px;
	padding: 10px 14px;
	border-bottom: 1px solid var(--cq-border-light);
	background: var(--cq-bg);
}
.cq-section-icon {
	width: 22px; height: 22px;
	display: inline-flex; align-items: center; justify-content: center;
	border-radius: 5px;
}
.cq-section-icon-blue { background: rgba(24,144,255,0.1); color: var(--cq-primary); }
.cq-section-icon-green { background: rgba(82,196,26,0.1); color: var(--cq-success); }
.cq-section-icon-purple { background: rgba(114,46,209,0.1); color: var(--cq-purple); }
.cq-section-title {
	font-size: 13px; font-weight: 600; color: var(--cq-text-1);
}
.cq-section-btn { margin-left: auto; }
.cq-section-body { padding: 14px; }

/* 信息列表 */
.cq-info-list { display: flex; flex-direction: column; gap: 10px; }
.cq-info-row { display: flex; align-items: center; gap: 10px; font-size: 13px; }
.cq-info-label {
	color: var(--cq-text-3); min-width: 56px; flex-shrink: 0;
}
.cq-info-value { color: var(--cq-text-1); }
.cq-info-value-strong { font-weight: 600; }

/* 录音 */
.cq-audio { width: 100%; }

/* 空状态 */
.cq-empty-block {
	padding: 24px 16px;
	text-align: center;
	color: var(--cq-text-4);
	font-size: 13px;
	background: var(--cq-bg);
	border-radius: 6px;
}
.cq-empty-block .ivu-icon { margin-bottom: 6px; color: var(--cq-text-4); }
.cq-empty-inline { color: var(--cq-text-4); font-size: 12px; }

/* AI 元信息 */
.cq-ai-meta {
	display: flex; gap: 16px; flex-wrap: wrap;
	padding-bottom: 12px; margin-bottom: 12px;
	border-bottom: 1px dashed var(--cq-border-light);
}
.cq-ai-meta-item { display: flex; align-items: center; gap: 6px; }
.cq-ai-error {
	background: #fff1f0;
	border: 1px solid #ffccc7;
	border-radius: 6px;
	padding: 8px 12px;
	color: var(--cq-danger);
	font-size: 13px;
	margin-bottom: 12px;
	display: flex; align-items: center; gap: 6px;
}

/* 标签组 */
.cq-tag-group { margin-bottom: 12px; }
.cq-tag-group:last-child { margin-bottom: 0; }
.cq-tag-group-head {
	display: flex; align-items: center; gap: 6px;
	font-size: 12px; color: var(--cq-danger);
	font-weight: 500;
	margin-bottom: 6px;
}
.cq-tag-group-head-green { color: var(--cq-success); }
.cq-tag-group-count {
	background: var(--cq-bg);
	color: var(--cq-text-3);
	padding: 1px 6px;
	border-radius: 10px;
	font-size: 11px;
	font-weight: 500;
}
.cq-tag-group-body { display: flex; flex-wrap: wrap; gap: 4px; }
.cq-tag-violation { margin: 0; }
.cq-tag-need { margin: 0; }

/* 对话气泡 */
.cq-dialogue-block { margin-top: 4px; }
.cq-dialogue-head {
	font-size: 12px; color: var(--cq-text-3);
	font-weight: 500;
	margin-bottom: 8px;
	display: flex; align-items: center;
}
.cq-dialogue-head::before {
	content: '';
	width: 3px; height: 12px;
	background: var(--cq-primary);
	margin-right: 6px;
	border-radius: 2px;
}
.cq-dialogue-list {
	background: var(--cq-bg);
	border-radius: 6px;
	padding: 12px;
	max-height: 320px;
	overflow-y: auto;
	display: flex; flex-direction: column; gap: 10px;
}
.cq-bubble {
	max-width: 80%;
	display: flex; flex-direction: column;
	gap: 2px;
}
.cq-bubble-agent { align-self: flex-start; }
.cq-bubble-client { align-self: flex-end; align-items: flex-end; }
.cq-bubble-meta { display: flex; align-items: center; gap: 6px; padding: 0 2px; }
.cq-bubble-name {
	font-size: 11px; color: var(--cq-text-3);
}
.cq-bubble-agent .cq-bubble-name { color: var(--cq-primary); }
.cq-bubble-client .cq-bubble-name { color: var(--cq-success); }
.cq-bubble-text {
	padding: 8px 12px;
	border-radius: 8px;
	font-size: 13px;
	line-height: 1.6;
	color: var(--cq-text-1);
	word-break: break-word;
	white-space: pre-wrap;
}
.cq-bubble-agent .cq-bubble-text {
	background: var(--cq-card-bg);
	border: 1px solid var(--cq-border-light);
	border-top-left-radius: 2px;
}
.cq-bubble-client .cq-bubble-text {
	background: var(--cq-primary);
	color: #fff;
	border-top-right-radius: 2px;
}
::v-deep .cq-bubble-client .violation-mark {
	background: rgba(255,255,255,0.25);
	color: #fff;
	border-color: rgba(255,255,255,0.4);
}

/* 转写文本（无对话明细时） */
.cq-ai-text {
	white-space: pre-wrap;
	word-break: break-all;
	background: var(--cq-bg);
	padding: 12px;
	border-radius: 6px;
	max-height: 320px;
	overflow-y: auto;
	line-height: 1.7;
	font-size: 13px;
	color: var(--cq-text-1);
}

/* 抽屉底部 */
.drawer-footer { text-align: right; }

/* ============ 违规词高亮 ============ */
::v-deep .violation-mark {
	background: #fff1f0;
	color: var(--cq-danger);
	font-weight: 600;
	padding: 1px 4px;
	border-radius: 3px;
	border: 1px solid #ffccc7;
}

/* ============ 响应式 ============ */
@media (max-width: 1700px) {
	.cq-page { max-width: 100%; }
}
@media (max-width: 1280px) {
	.cq-filter { grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 1200px) {
	.cq-charts { grid-template-columns: 1fr; }
	.cq-cols { grid-template-columns: 1fr; }
	.chart-canvas-trend { height: 320px; }
	.chart-canvas-violation { height: 300px; }
}
@media (max-width: 900px) {
	.cq-stats { grid-template-columns: repeat(2, 1fr); }
	.cq-filter { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 768px) {
	.cq-page { padding: 16px; }
	.cq-overview { grid-template-columns: repeat(2, 1fr); }
	.chart-canvas-trend,
	.chart-canvas-violation { height: 260px; }
	.cq-header { flex-wrap: wrap; gap: 12px; }
	.cq-header-right { width: 100%; justify-content: space-between; }
	.stat-card { padding: 16px 18px; }
}
@media (max-width: 480px) {
	.cq-stats { grid-template-columns: 1fr; }
	.cq-filter { grid-template-columns: 1fr; }
}
</style>